export const MAX_IMAGE_DIMENSION = 1920;

export const JPEG_QUALITY = 0.85;

export interface ResizeImageOptions {
  preserveTransparency?: boolean;
}

function loadImage(source: File | string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to load image'));
    if (typeof source === 'string') {
      img.src = source;
    } else {
      img.src = URL.createObjectURL(source);
    }
  });
}

export async function resizeImageForUpload(
  input: File | string,
  options: ResizeImageOptions = {}
): Promise<File> {
  const { preserveTransparency = false } = options;

  let file: File;
  let objectUrl: string | null = null;

  if (typeof input === 'string') {
    if (!input.startsWith('data:image/')) {
      throw new Error('resizeImageForUpload: string input must be a data URL');
    }
    const mime = input.match(/data:(image\/[^;]+)/)?.[1] || 'image/jpeg';
    const ext = mime === 'image/png' ? 'png' : 'jpg';
    file = new File(
      [Uint8Array.from(atob(input.split(',')[1]), (c) => c.charCodeAt(0))],
      `image.${ext}`,
      { type: mime }
    );
  } else {
    file = input;
  }

  if (!file.type.startsWith('image/')) {
    return file;
  }

  try {
    const source = typeof input === 'string' ? input : (objectUrl = URL.createObjectURL(file));
    const img = await loadImage(source);
    if (objectUrl) URL.revokeObjectURL(objectUrl);

    const w = img.naturalWidth;
    const h = img.naturalHeight;
    const scale = Math.min(1, MAX_IMAGE_DIMENSION / Math.max(w, h));
    const outW = Math.round(w * scale);
    const outH = Math.round(h * scale);

    const usePng = preserveTransparency;
    const canvas = document.createElement('canvas');
    canvas.width = outW;
    canvas.height = outH;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas 2d context not available');
    if (!usePng) {
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, outW, outH);
    }
    ctx.drawImage(img, 0, 0, outW, outH);

    const mimeType = usePng ? 'image/png' : 'image/jpeg';
    const ext = usePng ? 'png' : 'jpg';

    const blob = await new Promise<Blob | null>((resolveBlob) => {
      canvas.toBlob(
        resolveBlob,
        mimeType,
        usePng ? undefined : JPEG_QUALITY
      );
    });

    if (!blob) throw new Error('canvas.toBlob failed');

    const baseName = file.name.replace(/\.[^.]+$/, '') || 'image';
    const filename = `${baseName}.${ext}`;
    return new File([blob], filename, { type: mimeType });
  } catch (error) {
    console.error('Error resizing image, uploading original:', error);
    return file;
  }
}
